import { useState } from 'react'
import {
  Building2, AlertTriangle, Boxes, CalendarDays, ClipboardCheck, ClipboardList,
  Tags,
  PackageCheck, Shirt, Warehouse,
  FileText, Gauge, HeartPulse, Languages, Laptop, Layers, LayoutGrid, QrCode, Rocket,
  Bell, History, ScrollText, Settings2,
  ShieldCheck, Siren,
  UserCog, Users,
  Users2,
} from 'lucide-react'
import {
  AppShell, LocaleProvider, ThemeProvider, type AppLocale, type NavGroup,
} from '@hk/ui'
import { ActivityLog } from '../screens/ActivityLog'
import { System } from '../screens/System'
import { Devices } from '../screens/Devices'
import { Notifications } from '../screens/Notifications'
import { InspectionQueue } from '../screens/InspectionQueue'
import { Replenishment } from '../screens/Replenishment'
import { Laundry } from '../screens/Laundry'
import { StockPlaces } from '../screens/StockPlaces'
import { StockItems } from '../screens/StockItems'
import { Ledger } from '../screens/Ledger'
import { Operations } from '../screens/Operations'
import { RoomBoard } from '../screens/RoomBoard'
import { AreaBoard } from '../screens/AreaBoard'
import { AssignmentBoard } from '../screens/AssignmentBoard'
import { Roster } from '../screens/Roster'
import { Teams } from '../screens/Teams'
import { ServiceBoard } from '../screens/ServiceBoard'
import { Escalations } from '../screens/Escalations'
import { StockBoard } from '../screens/StockBoard'
import { Configuration } from '../screens/Configuration'
import { Translations } from '../screens/Translations'
import { GoLive } from '../screens/GoLive'
import { LinenStandards } from '../screens/LinenStandards'
import { Reports } from '../screens/Reports'
import { RoomSetup } from '../screens/RoomSetup'
import { QrCodes } from '../screens/QrCodes'
import { Checklists } from '../screens/Checklists'
import { StockCounts } from '../screens/StockCounts'
import { Users as UsersScreen } from '../screens/Users'
import { RolesPermissions } from '../screens/RolesPermissions'
import { STRINGS } from '../i18n/strings'
import {
  sessionOrNull, currentPropertyId, setCurrentProperty, clearSession, may, mayAny,
  propertyName,
} from '@hk/core'
import { PreviewBanner } from './PreviewBanner'
import { SignIn } from './SignIn'

/* The portal frame.

   Seven screens are built. The other twenty destinations are deliberately absent rather
   than present-and-dead: a sidebar full of links that do nothing is how a demo gets
   mistaken for a product, and this build is shown to the client.

   M-05 IS NOT HERE ANY MORE, AND THAT IS THE POINT. It lived here as a browser preview
   while the mobile stack was unconfirmed. The stack was confirmed on 8 September and the
   screen now exists for real in `app/mobile`, so keeping this copy would have meant two
   M-05s — and the one that drifts is always the one nobody runs. The handset is
   reviewed by running it, or from the proofs in `renders/handset/`. */

/* THE PROPERTY COMES FROM THE SESSION, not from a build-time variable.

   It used to be an env var with a zero-guid default, which was the only thing available
   before sign-in existed. Reading it from the session is not a tidy-up: a property id
   the CLIENT chooses is a property id the client can change, and the whole scoping
   model rests on that being impossible. */

/* THE APPROVED CODES, and they are the keys rather than free-text names so that a
   screen cannot be referred to by two different labels in the same application.
   Settled 8 Sep 2026: the Screen DESIGN packs are authoritative and the Inventory is
   reissued at Rev 02 to match — see SCREENS.md. W-60 covers what the packs draw as
   two screens, W-60 requests and W-70 faults; that merge is Change Note 02. */
type ScreenKey =
  | 'W-10' | 'W-20' | 'W-30' | 'W-40' | 'W-41' | 'W-42' | 'W-60' | 'W-61'
  | 'W-50' | 'W-108' | 'W-103' | 'W-109' | 'W-96' | 'W-101'
  | 'W-100' | 'W-105' | 'W-107' | 'W-110' | 'W-120' | 'W-122' | 'W-124' | 'W-125'
  | 'W-77'
  | 'W-127' | 'W-128' | 'W-129' | 'W-131' | 'W-134' | 'W-135' | 'W-136'

export function App() {
  const [locale, setLocale] = useState<AppLocale>('en')
  const [active, setActive] = useState<ScreenKey>('W-20')
  const [signedIn, setSignedIn] = useState(sessionOrNull() !== null)
  /* Bumped when the hotel changes, purely to remount the screens — see the picker. */
  const [propertyEpoch, setPropertyEpoch] = useState(0)
  const t = STRINGS[locale]

  if (!signedIn) {
    return (
      <ThemeProvider>
        <LocaleProvider locale={locale}>
          <SignIn
            locale={locale}
            onSignedIn={() => {
              // The person's own language preference, which is a property of THEM and
              // not of the browser. An attendant who reads Arabic should not have to
              // find the toggle every morning.
              const session = sessionOrNull()
              if (session?.preferredLanguage === 'ar') setLocale('ar')
              setSignedIn(true)
            }}
          />
        </LocaleProvider>
      </ThemeProvider>
    )
  }

  const propertyId = currentPropertyId()
  const session = sessionOrNull()
  /* **THE HOTEL THIS SCREEN IS ABOUT**, which since Sprint 48 is not necessarily the
     first one. It read `properties[0]` — right for everybody who works at one hotel, and
     wrong for Faiz e Husaini, whose administrators hold roles at both Najaf and Karbala
     and had no way to reach the second. */
  const property = session?.properties.find((p) => p.propertyId === propertyId) ?? null

  const nav: NavGroup[] = [
    {
      key: 'rooms',
      label: locale === 'ar' ? 'الغرف والعمل' : 'Rooms and work',
      tone: 'var(--hk-primary)',
      items: [
        /* **W-10 SITS ABOVE THE BOARD, AND ONLY FOR SOMEBODY WHOSE JOB IT IS.** It is
           the manager's landing screen — "can we cover today" is a question asked by the
           person who can do something about the answer. An attendant is given the rooms
           he is cleaning; a summary of the whole hotel is not his to carry, and a link
           that answers a question he was not asked is one more thing to scroll past.

           **IT IS NOT THE DEFAULT SCREEN**, deliberately. `W-20` still is, because the
           default has to be right for everybody who signs in, and most of them are on a
           floor. A manager reaches this in one click.

           **AND THE FRONT DESK IS IN THE GATE ON PURPOSE.** `room.viewReadiness` is held
           by the front desk and by nobody else, and "can we cover today" is the front
           desk's question before it is anybody's — they are the ones who have to tell a
           guest. Excluding the one role whose grant is literally named after readiness,
           from the screen that summarises readiness, would have been a rule followed past
           the point where it means anything. */
        ...(may('task.assign') || mayAny('report.') || may('room.viewReadiness')
          ? [{ key: 'W-10', label: t.opsTitle, icon: <Gauge className="size-3.5" /> }]
          : []),
        { key: 'W-20', label: t.boardTitle, icon: <LayoutGrid className="size-3.5" /> },
        /* **DEFECT #164 — THIS ENTRY WAS OFFERED TO EVERYBODY.** It was the last
           ungated link in this sidebar, and a room attendant who signed in on the web
           found the supervisor's board waiting for him: the lanes, his own rooms, and a
           "Generate today's work" button that answered *"your role does not allow this"*.

           **HIS WORK IS ON THE HANDSET AND ALWAYS WAS.** He holds `task.start` and
           `task.submit`, which are M-05's, and nothing on this screen. Every other entry
           in this group already follows the rule this one broke: a link to a screen full
           of buttons the person is refused teaches them to distrust the whole sidebar. */
        ...(may('task.assign')
          ? [{ key: 'W-40', label: t.assignTitle, icon: <Users className="size-3.5" /> }]
          : []),
        /* Public areas. Shown to anybody who can record a clean OR assign work — which
           is everybody who has a reason to look at it, and nobody who does not: a front
           desk clerk has no more use for a corridor's cadence than for a room's
           checklist. */
        ...(may('area.clean') || may('task.assign')
          ? [{ key: 'W-30', label: t.areasTitle, icon: <Building2 className="size-3.5" /> }]
          : []),
        /* THE ROSTER SITS BESIDE THE ASSIGNMENT BOARD, not in a settings corner, because
           it is the screen a supervisor opens when the board is wrong — somebody is
           missing from it, or somebody is on it who called in sick. Filing it under
           administration would put it where nobody looks at seven in the morning. */
        ...(may('roster.edit') || may('task.assign')
          ? [{ key: 'W-41', label: t.rosterTitle, icon: <CalendarDays className="size-3.5" /> }]
          : []),
        /* TEAMS SITS BESIDE THE ROSTER, for the same reason the roster sits beside the
           board: it is where a supervisor goes when the morning needs setting up or a man
           has not come in. Under `task.assign`, because giving a floor to a pair IS
           assignment — the same act as dragging a card, at a different scale. */
        ...(may('task.assign')
          ? [{ key: 'W-42', label: t.teamsTitle, icon: <Users2 className="size-3.5" /> }]
          : []),
        /* SHOWN ONLY TO SOMEBODY WHO CAN ACT ON IT. A link that leads to a screen
           full of buttons the person is refused is worse than no link — they click
           it, get told no, and learn to distrust the whole sidebar. Front desk and
           attendants raise these; supervisors and managers work them. */
        ...(may('task.inspect') || may('request.raise')
          ? [{ key: 'W-60', label: t.issuesTitle, icon: <AlertTriangle className="size-3.5" /> }]
          : []),
        /* **NEEDS YOU — THE WEB'S HALF OF M-15 (Sprint 47).** Pack 6 says the four cards
           are *"the same four a supervisor sees on the web"*, so this entry and the
           handset's screen shipped together. Shown to whoever can inspect: the four
           things on it are all a supervisor's to move, and a front desk clerk offered a
           screen he can do nothing about learns to ignore the sidebar. */
        ...(may('task.inspect')
          ? [{ key: 'W-61', label: t.escalationsTitle, icon: <Siren className="size-3.5" /> }]
          : []),
        /* **W-50 — WHAT IS WAITING FOR AN INSPECTOR** (Sprint 65). Gated on `task.inspect`
           for the reason the two entries above give: a link to a screen of buttons somebody
           is refused teaches them to distrust the whole sidebar. A queue is not quite that
           — the rows are readable — but every row on it exists to be acted on, and a person
           who can act on none of them has been sent to a waiting room. */
        ...(may('task.inspect')
          ? [{ key: 'W-50', label: t.iqNav, icon: <ClipboardCheck className="size-3.5" /> }]
          : []),
        /* **W-77 IS GATED ON NOTHING** (Sprint 75), and that is the only honest gate it
           could have. Every row on it is about the reader's own work, selected by the
           reader's own id — there is no parameter naming whose, so there is nothing a
           capability could widen or narrow, and a cell on W-122 reading "may see what was
           done to their own rooms" is a cell nobody could explain. It sits last in this
           group because it is the only entry here about the PAST. */
        { key: 'W-77', label: t.ntfTitle, icon: <Bell className="size-3.5" /> },
      ],
    },
    /* INVENTORY IS ITS OWN GROUP FROM SPRINT 24, and moving W-100 into it is a
       correction towards the pack rather than a rearrangement. Pack 3 draws an
       Inventory section and this build had stock filed under "Rooms and work", which
       was defensible while there was one stock screen and is not now there are two: a
       store keeper's entire job would have appeared as a single link inside a group
       named after somebody else's. */
    ...(may('stock.move') || may('stock.adjust') || may('report.attendantProductivity')
      ? [{
          key: 'inventory',
          label: locale === 'ar' ? 'المخزون' : 'Inventory',
          tone: 'var(--hk-primary)',
          items: [
            /* Shown to whoever can move stock, and to a manager who cannot but needs
               the loss figure. */
            ...(may('stock.move') || may('report.attendantProductivity')
              ? [{ key: 'W-100', label: t.stockTitle, icon: <Boxes className="size-3.5" /> }]
              : []),
            /* W-106 IS NOT IN THE SIDEBAR, deliberately — the same reasoning as W-126.
               A variance has no meaning without a count chosen, so it is reached from
               one. And the entry is shown to a MANAGER as well as a store keeper,
               because approving is the manager's half of this screen: the person who
               counted the shortfall is refused it. */
            /* **W-108 — WHAT TO SEND UP THIS MORNING** (Sprint 67). Shown to whoever can
               move stock, because every row on it is a movement he is about to make. A
               manager who cannot move stock can still read it, but the sidebar is for the
               person whose morning this is. */
            ...(may('stock.move')
              ? [{ key: 'W-108', label: t.rpTitle, icon: <PackageCheck className="size-3.5" /> }]
              : []),
            /* **W-103 — AT THE LAUNDRY** (Sprint 68). Same gate as the picking list: the
               person who sends the linen down is the person who needs to know it has not
               come back. A read rather than an act, so it is not gated more tightly than
               the board it is read beside. */
            ...(may('stock.move')
              ? [{ key: 'W-103', label: t.lnTitle, icon: <Shirt className="size-3.5" /> }]
              : []),
            /* **W-109 — THE PLACES** (Sprint 69). Behind `admin.stockPars`, not
               `stock.move`: creating a pantry and deciding what it should hold are the same
               decision at two moments, and carrying stock up to it is somebody else's
               morning. */
            ...(may('admin.stockPars')
              ? [{ key: 'W-109', label: t.plTitle, icon: <Warehouse className="size-3.5" /> }]
              : []),
            /* **W-96 — THE ITEMS** (Sprint 80). The same grant and beside the places, for
               the reason the places sit behind `admin.stockPars`: deciding the hotel
               issues slippers, deciding where they are kept and deciding how many belong
               on the fourth floor are one decision at three moments. */
            ...(may('admin.stockPars')
              ? [{ key: 'W-96', label: t.itTitle, icon: <Tags className="size-3.5" /> }]
              : []),
            /* **W-101 — THE LEDGER** (Sprint 80). Two grants, not one and not a new
               third: `stock.move` is the store keeper's and `stock.adjust` is the
               manager's, and the seed gives the manager only the second. Either alone
               would shut one of them out of the evidence behind a figure they are
               required to act on. */
            ...(may('stock.move') || may('stock.adjust')
              ? [{ key: 'W-101', label: t.lgTitle, icon: <History className="size-3.5" /> }]
              : []),
            ...(may('stock.move') || may('stock.adjust')
              ? [{ key: 'W-105', label: t.cntTitle, icon: <ClipboardCheck className="size-3.5" /> }]
              : []),
          ],
        }]
      : []),
    /* A GROUP OF ITS OWN, and it is opened by a FAMILY of permissions rather than by a
       report code. Reporting is one screen and eight definitions: the day the ninth is
       added, a sidebar that named a report would be one forgotten edit away from hiding
       it from the people who may run it, and nothing would fail. The group disappears
       entirely for somebody who may run none — an empty heading is worse than no heading.
       Separate from "Rooms and work" because it is a different act: that group is what
       you do this morning, and this is what you take to a meeting. */
    ...(mayAny('report.') || may('admin.rooms') || may('admin.qrCodes')
        || may('admin.checklists') || may('admin.users') || may('admin.roles')
        || may('admin.activity') || may('admin.goLive') || may('admin.configuration')
      ? [{
          key: 'management',
          /* THE SECTION IS NOT CALLED "REPORTS", because the screen inside it is. A
             heading and its only item reading the same word twice looks like a mistake
             on screen — and it was one: the first render of this sidebar had "Reports"
             above "Reports". The section is named for what will fill it, which is the
             administration screens and the stock count still to come. */
          label: locale === 'ar' ? 'الإدارة' : 'Management',
          tone: 'var(--hk-primary)',
          items: [
            /* **CQ-136 — WHERE A HOTEL CORRECTS ITS OWN LINEN STANDARDS.** Under
               Management rather than Inventory: it is a statement about how a room is
               cleaned, which is the manager's, and it happens to be counted in stock. */
            ...(may('admin.linenStandards')
              ? [{ key: 'W-107', label: t.linenTitle, icon: <Layers className="size-3.5" /> }]
              : []),
            ...(mayAny('report.')
              ? [{ key: 'W-110', label: t.reportsTitle, icon: <FileText className="size-3.5" /> }]
              : []),
            /* W-127 IS TWO SCREENS BEHIND ONE ENTRY: the spreadsheet that puts five
               hundred rooms in before go-live, and the form that changes one room every
               week afterwards. One entry, because they are the same job at two scales —
               and because a separate "load rooms" link is one somebody clicks who only
               meant to renumber 305. */
            ...(may('admin.rooms')
              ? [{ key: 'W-127', label: t.setupTitle, icon: <Building2 className="size-3.5" /> }]
              : []),
            /* **W-135 SITS AFTER THE ROOM MASTER, BECAUSE IT READS WHAT THAT SCREEN
               FILLS.** It is the only entry in this sidebar with a life expectancy: one
               week, and then never opened again. It is not hidden after that week — a
               hotel opening a second property goes through the same list, and Karbala is
               exactly that. */
            ...(may('admin.goLive')
              ? [{ key: 'W-135', label: t.glTitle, icon: <Rocket className="size-3.5" /> }]
              : []),
            /* **W-129 SITS BESIDE THE GO-LIVE CHECKLIST**, because they are the same week's
               work: one says what is missing and the other is where the hotel's own words
               get put right. CQ-130's answer — "they will update them from the settings" —
               is only true because this entry exists. */
            /* **EITHER PERMISSION OPENS IT** (Sprint 56). The screen holds two cards with
               two different owners: the administrator's hotel details and role names, and
               the housekeeping manager's list of what a guest may ask for. A nav entry
               gated on the administrator's permission alone would leave the manager holding
               a right with no way to reach it. */
            ...(may('admin.configuration') || may('admin.requestCategories')
              ? [{ key: 'W-129', label: t.cfgTitle, icon: <Settings2 className="size-3.5" /> }]
              : []),
            /* **W-128 SITS BESIDE THE SETTINGS**, because the two screens are halves of one
               job: correcting every word this product invented. Its own permission, held by
               the housekeeping manager as well — the person who knows what the floor calls a
               bath towel is on the floor. */
            ...(may('admin.translations')
              ? [{ key: 'W-128', label: t.trTitle, icon: <Languages className="size-3.5" /> }]
              : []),
            /* W-136 sits BESIDE the room master, because that is the order the work
               happens in: the rooms go in, then the cards are issued for them. Its own
               permission — issuing and revoking the cards in the bedrooms is not the same
               right as deciding how many rooms the hotel has. */
            ...(may('admin.qrCodes')
              ? [{ key: 'W-136', label: t.qrTitle, icon: <QrCode className="size-3.5" /> }]
              : []),
            /* W-126 IS NOT IN THE SIDEBAR, deliberately. The editor is reached by pressing
               edit on a template and has no meaning without one chosen — a "template
               editor" link would open onto nothing. */
            ...(may('admin.checklists')
              ? [{ key: 'W-125', label: t.clTitle, icon: <ClipboardList className="size-3.5" /> }]
              : []),
            /* W-121 IS NOT IN THE SIDEBAR — the same reasoning as W-126 and W-106. One
               person's detail has no meaning without a person chosen, so it is reached
               from the list. */
            ...(may('admin.users')
              ? [{ key: 'W-120', label: t.usrTitle, icon: <UserCog className="size-3.5" /> }]
              : []),
            /* W-122 SITS BESIDE THE USERS LIST, because they are two halves of one
               question: that screen says which role a person holds, this one says what
               a role may do, and an access problem at eight in the morning is answered
               by one or the other. Its OWN permission, because they are not the same
               right: giving somebody the supervisor role is a daily act, and changing
               what a supervisor may do is not. */
            ...(may('admin.roles')
              ? [{ key: 'W-122', label: t.rolTitle, icon: <ShieldCheck className="size-3.5" /> }]
              : []),
            /* W-124 IS LAST IN THE GROUP, and that is the order of the work rather than an
               afterthought: the screens above it are where somebody CHANGES something, and
               this is where somebody reads what was changed. Its own permission, held by
               fewer people than any of them — including, deliberately, not by the floor
               supervisor, whose overrides are the most-read rows in it. */
            ...(may('admin.activity')
              ? [{ key: 'W-124', label: t.actTitle, icon: <ScrollText className="size-3.5" /> }]
              : []),
            /* **W-131 SHARES W-124's PERMISSION AND SITS BENEATH IT** (Sprint 70), because
               the two answer one question between them. This screen says whether the log is
               being written to something that survives the server being replaced; that one
               is the log. A person who may read the second is the person who needs to know
               the first, and inventing a separate right would have meant an administrator
               who can read the overrides and cannot see that they are on a disk nobody has
               confirmed is backed up. */
            ...(may('admin.activity')
              ? [{ key: 'W-131', label: t.sysTitle, icon: <HeartPulse className="size-3.5" /> }]
              : []),
            /* **W-134 IS BEHIND `admin.users`, NOT `admin.activity`** (Sprint 71), because
               the only act on it is ending somebody's session — which is the users
               screen's act, guarded by the users screen's right. Reading the register and
               being able to do the one thing it offers are not two decisions. */
            ...(may('admin.users')
              ? [{ key: 'W-134', label: t.dvTitle, icon: <Laptop className="size-3.5" /> }]
              : []),
          ],
        }]
      : []),
  ]

  const screen =
    active === 'W-10'
      ? { title: t.opsTitle, subtitle: t.opsSubtitle, icon: <Gauge className="size-5" /> }
    : active === 'W-20'
      ? { title: t.boardTitle, subtitle: t.boardSubtitle, icon: <LayoutGrid className="size-5" /> }
      : active === 'W-30'
        ? { title: t.areasTitle, subtitle: t.areasSubtitle, icon: <Building2 className="size-5" /> }
      : active === 'W-40'
        ? { title: t.assignTitle, subtitle: t.assignSubtitle, icon: <Users className="size-5" /> }
      : active === 'W-41'
        ? { title: t.rosterTitle, subtitle: t.rosterSubtitle, icon: <CalendarDays className="size-5" /> }
      : active === 'W-42'
        ? { title: t.teamsTitle, subtitle: t.teamsSubtitle, icon: <Users2 className="size-5" /> }
        : active === 'W-60'
          ? { title: t.issuesTitle, subtitle: t.requestsTitle, icon: <AlertTriangle className="size-5" /> }
      /* **DEFECT #153 — "NEEDS YOU" WORE THE STOCK SCREEN'S NAME.** W-61 had no branch in
         this chain, so it fell through to the final `else` and every supervisor who opened
         the alerts saw a header reading *"Linen and stock · Linen stays ours when it goes
         to a room"* above four cards about rooms, guests and faults.

         **THE FALL-THROUGH IS THE DEFECT, NOT THE MISSING LINE.** A chain whose last
         `else` is a real screen hands its identity to every screen somebody forgets, and
         forgets silently — the screen renders, nothing throws, and the only thing that
         notices is a person reading the top of the page. Found by screenshotting it.
         `headerContractTests` now holds every nav key against this chain by name. */
      : active === 'W-109'
        ? { title: t.plTitle, subtitle: t.plSubtitle, icon: <Warehouse className="size-5" /> }
      : active === 'W-96'
        ? { title: t.itTitle, subtitle: t.itSubtitle, icon: <Tags className="size-5" /> }
      : active === 'W-101'
        ? { title: t.lgTitle, subtitle: t.lgSubtitle, icon: <History className="size-5" /> }
      : active === 'W-103'
        ? { title: t.lnTitle, subtitle: t.lnSubtitle, icon: <Shirt className="size-5" /> }
      : active === 'W-108'
        ? { title: t.rpTitle, subtitle: t.rpSubtitle, icon: <PackageCheck className="size-5" /> }
      : active === 'W-50'
        ? { title: t.iqTitle, subtitle: t.iqSubtitle, icon: <ClipboardCheck className="size-5" /> }
      : active === 'W-61'
        ? { title: t.escalationsTitle, subtitle: t.escalationsSubtitle,
            icon: <Siren className="size-5" /> }
      : active === 'W-107'
        ? { title: t.linenTitle, subtitle: t.linenSubtitle, icon: <Layers className="size-5" /> }
      : active === 'W-110'
        ? { title: t.reportsTitle, subtitle: t.reportsSubtitle, icon: <FileText className="size-5" /> }
      : active === 'W-127'
        ? { title: t.setupTitle, subtitle: t.setupSubtitle, icon: <Building2 className="size-5" /> }
      : active === 'W-129'
        ? { title: t.cfgTitle, subtitle: t.cfgSubtitle, icon: <Settings2 className="size-5" /> }
      : active === 'W-128'
        ? { title: t.trTitle, subtitle: t.trSubtitle, icon: <Languages className="size-5" /> }
      : active === 'W-135'
        ? { title: t.glTitle, subtitle: t.glSubtitle, icon: <Rocket className="size-5" /> }
      : active === 'W-136'
        ? { title: t.qrTitle, subtitle: t.qrSubtitle, icon: <QrCode className="size-5" /> }
      : active === 'W-124'
        ? {
            title: t.actTitle,
            subtitle: t.actSubtitle.replace('{months}', locale === 'ar' ? '٢٤' : '24'),
            icon: <ScrollText className="size-5" />,
          }
      : active === 'W-77'
        ? { title: t.ntfTitle, subtitle: t.ntfSubtitle, icon: <Bell className="size-5" /> }
      : active === 'W-131'
        ? { title: t.sysTitle, subtitle: t.sysSubtitle, icon: <HeartPulse className="size-5" /> }
      : active === 'W-134'
        ? { title: t.dvTitle, subtitle: t.dvSubtitle, icon: <Laptop className="size-5" /> }
      : active === 'W-125'
        ? { title: t.clTitle, subtitle: t.clSubtitle, icon: <ClipboardList className="size-5" /> }
      : active === 'W-105'
        ? { title: t.cntTitle, subtitle: t.cntSubtitle, icon: <ClipboardCheck className="size-5" /> }
      : active === 'W-120'
        ? { title: t.usrTitle, subtitle: t.usrSubtitle, icon: <UserCog className="size-5" /> }
      : active === 'W-122'
        ? { title: t.rolTitle, subtitle: t.rolSubtitle, icon: <ShieldCheck className="size-5" /> }
      /* **THE LAST BRANCH NAMES ITS SCREEN** (#153). It used to be a bare `else` holding
         W-100's header, which is how W-61 came to wear it. The fallback below is now a
         screen that does not exist, so a forgotten header is a visible blank rather than
         a confident lie — and the contract test makes it not happen at all. */
      : active === 'W-100'
        ? { title: t.stockTitle, subtitle: t.stockOwnedSubtitle, icon: <Boxes className="size-5" /> }
          : { title: '', subtitle: '', icon: <Boxes className="size-5" /> }

  return (
    <ThemeProvider>
      <LocaleProvider locale={locale}>
        {/* Above the shell, not inside it: it must not scroll away with the content. */}
        <PreviewBanner locale={locale} />
        <AppShell
          nav={nav}
          active={active}
          onNavigate={(key) => setActive(key as ScreenKey)}
          /* THE HOTEL'S OWN NAME, FROM THE HOTEL'S OWN RECORD, IN THE READER'S LANGUAGE.

             This read `name: 'Faiz e Husaini'` — the client's name written straight into
             the markup, in Latin, above every screen including every Arabic one. Two
             defects in one line: a visible word that was never in a dictionary (#84's
             shape), and a product hardcoded to one customer.

             The subtitle read `properties[0].name`, which was real data and equally
             Latin, because `Property` had no Arabic column at all until Sprint 30 —
             found by a guard rather than by anybody looking at it. */
          brand={{
            name: property ? propertyName(property, locale) : '',
            sub: property?.code ?? '',
            /* THE PROPERTY CODE, WHOLE. It was `.slice(0, 2)` for about ten minutes,
               which turned NJF into NJ — and a truncated identifier is not an
               identifier, it is a different string that happens to look like one. The
               badge is 34px and three characters fit; the day a hotel uses a longer
               code, the badge is what changes. */
            mark: property?.code ?? '',
          }}
          title={screen.title}
          subtitle={screen.subtitle}
          icon={screen.icon}
          searchPlaceholder={t.searchPlaceholder}
          toolbar={
            <div className="flex items-center gap-2">
              {/* ══ WHICH HOTEL (Sprint 48) ═══════════════════════════════════════════

                  **SHOWN ONLY TO SOMEBODY WHO HOLDS A ROLE AT MORE THAN ONE.** A
                  housekeeper works at one hotel, and a picker with one entry in it is a
                  control that can only be got wrong.

                  **NAJAF AND KARBALA SHARE NOTHING** — the client's own answer on
                  17 September: separate staff, separate stores, separate boards. So this
                  is not a filter over one dataset, it is which hotel you are standing in,
                  and every screen behind it changes completely. */}
              {(session?.properties.length ?? 0) > 1 && (
                <select
                  value={propertyId}
                  onChange={(e) => {
                    setCurrentProperty(e.target.value)
                    /* **THE SCREENS ARE THROWN AWAY, NOT RE-FILTERED.** Everything on the
                       board belongs to the hotel that was showing when it was drawn; a
                       board still holding Najaf's rooms under Karbala's name is worse
                       than a moment's blank. `propertyEpoch` keys the shell. */
                    setPropertyEpoch((n) => n + 1)
                  }}
                  aria-label={locale === 'ar' ? 'الفندق' : 'Hotel'}
                  className="rounded-hk border border-border bg-card px-2 py-1 text-xs
                             font-medium hover:bg-muted cursor-pointer"
                >
                  {session!.properties.map((p) => (
                    <option key={p.propertyId} value={p.propertyId}>
                      {propertyName(p, locale)}
                    </option>
                  ))}
                </select>
              )}
              {/* Who you are signed in as, always visible. In a test build where anyone
                  can be anyone, "which user am I?" is the question asked most often. */}
              <span className="text-xs text-muted-foreground">
                <bdi>{locale === 'ar' ? session?.displayNameAr ?? session?.displayName : session?.displayName}</bdi>
              </span>
              <button
                type="button"
                onClick={() => { clearSession(); setSignedIn(false) }}
                className="rounded-hk border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted cursor-pointer"
              >
                {locale === 'ar' ? 'خروج' : 'Sign out'}
              </button>
              <button
                type="button"
                onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
                className="rounded-hk border border-border px-2.5 py-1 text-xs font-medium hover:bg-muted cursor-pointer"
              >
                {locale === 'en' ? 'العربية' : 'English'}
              </button>
            </div>
          }
        >
          {/* KEYED ON THE HOTEL. Changing it remounts whatever is showing, so no screen
              carries one hotel's rooms, lanes or stock into the other's name. */}
          <div key={`${propertyId}:${propertyEpoch}`} className="contents">
          {active === 'W-10' ? <Operations propertyId={propertyId} />
            : active === 'W-20' ? <RoomBoard propertyId={propertyId} />
            : active === 'W-30' ? <AreaBoard propertyId={propertyId} />
            : active === 'W-40' ? <AssignmentBoard propertyId={propertyId} />
            : active === 'W-41' ? <Roster propertyId={propertyId} />
            : active === 'W-42' ? <Teams propertyId={propertyId} />
            : active === 'W-50' ? <InspectionQueue propertyId={propertyId} />
            : active === 'W-108' ? <Replenishment propertyId={propertyId} />
            : active === 'W-103' ? <Laundry propertyId={propertyId} />
            : active === 'W-109' ? <StockPlaces propertyId={propertyId} />
            : active === 'W-96' ? <StockItems propertyId={propertyId} />
            : active === 'W-101' ? <Ledger propertyId={propertyId} />
            : active === 'W-61' ? <Escalations />
            : active === 'W-60' ? <ServiceBoard propertyId={propertyId} />
            : active === 'W-107' ? <LinenStandards propertyId={propertyId} />
            : active === 'W-110' ? <Reports propertyId={propertyId} />
            : active === 'W-127' ? <RoomSetup propertyId={propertyId} />
            : active === 'W-129' ? <Configuration propertyId={propertyId} />
            : active === 'W-128' ? <Translations propertyId={propertyId} />
            : active === 'W-135' ? <GoLive propertyId={propertyId} />
            : active === 'W-136' ? <QrCodes propertyId={propertyId} />
            : active === 'W-125' ? <Checklists propertyId={propertyId} />
            : active === 'W-105' ? <StockCounts propertyId={propertyId} />
            : active === 'W-120' ? <UsersScreen />
            : active === 'W-122' ? <RolesPermissions />
            : active === 'W-124' ? <ActivityLog />
            : active === 'W-131' ? <System propertyId={propertyId} />
            : active === 'W-134' ? <Devices propertyId={propertyId} />
            : active === 'W-77' ? <Notifications />
            : <StockBoard propertyId={propertyId} />}
          </div>
        </AppShell>
      </LocaleProvider>
    </ThemeProvider>
  )
}
